import { useState, useCallback, useEffect } from 'react';
import { Form } from 'antd';
import { useApplicationId } from '../../../hooks/useApplicationId';
import {
	Application,
	Feedback,
	UserRole,
} from '../../../shared/VolunteerApplicationServiceTypes';
import { useCallApi } from '../../../hooks/useCallApi';
import { submitZCRecommendation } from '../../../service/VolunteerApplicationService';
import { useAuth } from '../../../hooks/useAuth';
import { FUNCTIONARY_HOME, INTERVIEW_LANDING } from '../../../shared/Routes';
import { ReviewAction } from '../../../shared/Constants';
import { useConfirmPopup } from '../../../hooks/useConfirmPopup';
import { useInfoPopup } from '../../../hooks/useInfoPopUp';
import ConfirmTick from '../../../assets/images/confirm-tick.svg';
import useUserSession from '../../../hooks/useUserSession';
import { resetApplicationsListCache } from '../ApplicationsList/useApplicationsList';

export const useZCRecommendationPage = () => {
	const [recommendation, setRecommendation] = useState<Feedback>({});
	const [submitButtonDisabled, setSubmitButtonDisabled] =
		useState<boolean>(false);
	const [form] = Form.useForm<Feedback>();
	const values = Form.useWatch<Feedback>([], form);
	const { fetchApplication, applicationId, navigateWithApplicationId } =
		useApplicationId();
	const [application, setApplication] = useState<Application | undefined>();
	const [isApprover, setIsApprover] = useState<boolean | undefined>(false);
	const { callApi } = useCallApi();
	const { srcmId } = useAuth();
	const { selectedRole } = useUserSession();
	const { confirmPopUpAction } = useConfirmPopup();
	const { infoPopUpAction } = useInfoPopup();

	const onConfirmSubmit = useCallback(async () => {
		try {
			await callApi(
				() =>
					submitZCRecommendation({
						recommendation,
						applicationId,
						functionarySrcmId: srcmId as string,
						shouldCareTeamReview: recommendation.shouldCareTeamReview,
						selectedRoleCode: selectedRole as UserRole,
					}),
				'errorOccurredWhileSubmitting'
			);
			resetApplicationsListCache();
			infoPopUpAction(
				'submittedSuccessfully',
				'',
				() => {
					navigateWithApplicationId(FUNCTIONARY_HOME);
				},
				ConfirmTick
			);
		} catch (r) {
			// Do Nothing
		}
	}, [
		callApi,
		infoPopUpAction,
		recommendation,
		applicationId,
		srcmId,
		selectedRole,
		navigateWithApplicationId,
	]);

	const onSubmitClick = useCallback(() => {
		confirmPopUpAction(
			'confirmSubmit',
			'onceSubmittedFeedbackCannotBeChanged',
			onConfirmSubmit
		);
	}, [confirmPopUpAction, onConfirmSubmit]);

	const onCancelClick = useCallback(() => {
		navigateWithApplicationId(INTERVIEW_LANDING);
	}, [navigateWithApplicationId]);

	const handleFormValuesChange = useCallback(async () => {
		try {
			const fieldValues = await form.validateFields({ validateOnly: true });
			setRecommendation(fieldValues);
			if (
				fieldValues.review === ReviewAction.REJECT &&
				!fieldValues.predefinedReasons?.length
			) {
				setSubmitButtonDisabled(true);
			} else {
				setSubmitButtonDisabled(false);
			}
		} catch (e) {
			const fields = e as { values: Feedback };
			setRecommendation(fields.values);
			setSubmitButtonDisabled(true);
		}
	}, [form]);

	useEffect(() => {
		void handleFormValuesChange();
	}, [handleFormValuesChange, values]);

	const loadApplication = useCallback(async () => {
		try {
			const fetchedApplication: Application = await fetchApplication();
			setRecommendation(fetchedApplication?.zcRecommendation);
			setIsApprover(fetchedApplication?.isApprover);
			setApplication(fetchedApplication);

			form.setFieldsValue(fetchedApplication?.zcRecommendation);
		} catch (e) {
			//
		}
	}, [fetchApplication, form]);

	useEffect(() => {
		void loadApplication();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [form]);

	return {
		form,
		application,
		submitButtonDisabled,
		onSubmitClick,
		onCancelClick,
		isApprover,
	};
};
